import { linkOptions } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { v4 as uuidv4 } from "uuid";

import { authMiddleware, authOrCoachMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { S3Middleware } from "~/lib/s3-middleware";
import type { CoachEngagementCardsQuery } from "~/lib/schemas/engagement";
import {
  CoachClientRelationshipSchema,
  CoachEngagementCardsQuerySchema,
  CoachesClientsSchema,
  CreateEngagementSchema,
  CreateEngResourceAfterUploadSchema,
  DeleteEngagementInvoiceSchema,
  DeleteEngResourceSchema,
  EditEngagementInvoiceSchema,
  EditEngagementSchema,
  EngagementCoachIdSchema,
  EngagementResourcesByCoachSchema,
  EngagementResourceTagsSchema,
  FinalizeCreateEngResourceSchema,
  FinalizeUpdateEngResourceSchema,
  GetAllEngagementResourcesByUploadModelSchema,
  GetAllEngagementResourcesSchema,
  GetEngagementSessionProgressSchema,
  GetEngResourceDownloadInfoSchema,
  GetResourceUploadUrlSchema,
  GetUpdateEngResourceUploadUrlSchema,
  PaymentHistoryForEngagementSchema,
  PaymentInfoForEngagementSchema,
  UpdateEngagementStatusSchema,
  UserEngagementsSchema,
  UserInfoForEngagementSchema,
} from "~/lib/schemas/engagement";
import { buildNotificationLink } from "~/lib/shared/notification-link";

import type { EngagementStatus } from "../../../drizzle/schema";

export const getActiveEngagementsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(EngagementCoachIdSchema)
  .handler(async ({ context }) => {
    const { getActiveEngagementsForCoach } =
      await import("~/lib/server/engagement");
    return getActiveEngagementsForCoach(context.coach);
  });

export const getCoachEngagementCardsFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(CoachEngagementCardsQuerySchema)
  .handler(async ({ data, context }) => {
    const { getCoachEngagementCards } = await import("~/lib/server/engagement");
    const { page, limit } = data as CoachEngagementCardsQuery;
    const coachId = context.coach.id;
    return getCoachEngagementCards(coachId, { page, limit });
  });

export const getUserEngagementsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(UserEngagementsSchema)
  .handler(async ({ data, context }) => {
    const { getUserEngagements } = await import("~/lib/server/engagement");
    return getUserEngagements(
      context.user.id,
      { page: data.page, limit: data.limit },
      data.status,
    );
  });

export const getAllEngagementByCoachFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(CoachEngagementCardsQuerySchema)
  .handler(async ({ data, context }) => {
    const { getAllEngagementByCoachOrClient } =
      await import("~/lib/server/engagement");
    const { page, limit, sort, statuses } = data as CoachEngagementCardsQuery;
    const coachId = context.coach.id;
    return getAllEngagementByCoachOrClient(
      "coach",
      coachId,
      sort,
      { page, limit },
      statuses,
    );
  });

export const getCoachClientProgramSummaryFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }) => {
    const { getCoachClientProgramSummary } =
      await import("~/lib/server/engagement");
    return getCoachClientProgramSummary(context.coach.id);
  });

export const getAllEngagementByClientFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(CoachEngagementCardsQuerySchema)
  .handler(async ({ data, context }) => {
    const { getAllEngagementByCoachOrClient } =
      await import("~/lib/server/engagement");
    const { page, limit, sort, statuses } = data as CoachEngagementCardsQuery;
    const userId = context.user.id;
    return getAllEngagementByCoachOrClient(
      "user",
      userId,
      sort,
      { page, limit },
      statuses,
    );
  });

export const getAllClientCoachesFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(CoachesClientsSchema)
  .handler(async ({ data, context }) => {
    const { getAllClientCoaches } = await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getAllClientCoaches(userId, { ...data });
  });

export const createEngagementFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CreateEngagementSchema)
  .handler(async ({ data, context }) => {
    const { createEngagement } = await import("~/lib/server/engagement");
    return createEngagement(context.coach, data);
  });

export const getCoachClientRelationshipFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(CoachClientRelationshipSchema)
  .handler(async ({ data, context }) => {
    const { getCoachClientRelationship } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getCoachClientRelationship(coachId, { ...data });
  });

export const updateEngagementFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(EditEngagementSchema)
  .handler(async ({ data, context }) => {
    const { updateEngagement } = await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return updateEngagement(coachId, data);
  });

export const getEngResourceUploadUrlFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(GetResourceUploadUrlSchema)
  .handler(async ({ data, context }) => {
    const { buildKeyFromResourceId, getPresignedUploadUrl } =
      await import("~/lib/server/s3");
    const coachId = context.coach.id;
    if (!data.filename) throw new Error("File name is required");
    if (!data.contentType) throw new Error("ContentType is required");

    const resourceId = uuidv4();
    const key = buildKeyFromResourceId({
      coachId,
      engagementId: data.engagementId,
      resourceId: resourceId,
      filename: data.filename,
    });

    const signed = await getPresignedUploadUrl({
      key,
      contentType: data.contentType,
    });

    return {
      resourceId: resourceId,
      key: key,
      signed,
    };
  });

export const finalizeEngResourceFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(FinalizeCreateEngResourceSchema)
  .handler(async ({ data, context }) => {
    const { createEngagementResource } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return await createEngagementResource({
      newResource: {
        id: data.resourceId, // Use the provided resourceId from upload URL generation
        coachId,
        engagementId: data.engagementId,
        sessionId: data.sessionId,
        filename: data.filename,
        title: data.title,
        uploadedBy: "coach",
        uploadModel: data.uploadModel,
        expirationDate:
          data.uploadModel === "agreement" ? data.expirationDate : null,
      },
      tagIds: data.tagIds ?? [],
    });
  });

export const createEngResourceForUrlFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(CreateEngResourceAfterUploadSchema)
  .handler(async ({ data, context }) => {
    const { createEngagementResource } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;

    if (!data.externalLinkUrl) {
      throw new Error("External link is required");
    }
    const row = await createEngagementResource({
      newResource: {
        coachId,
        engagementId: data.engagementId,
        sessionId: data.sessionId,
        filename: data.name,
        uploadedBy: "coach",
        title: data.title,
        uploadModel: "url",
        externalLink: data.externalLinkUrl,
        publishedAt: new Date(),
      },
      tagIds: data.tagIds,
    });

    return row;
  });

export const getUpdateEngResourceUploadUrlFn = createServerFn({
  method: "POST",
})
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(GetUpdateEngResourceUploadUrlSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceById } =
      await import("~/lib/server/engagement");
    const { buildKeyFromResourceId, getPresignedUploadUrl } =
      await import("~/lib/server/s3");
    const coachId = context.coach.id;
    const resource = await getEngagementResourceById(data.resourceId, coachId);

    const key = buildKeyFromResourceId({
      coachId,
      engagementId: resource.engagementId,
      resourceId: resource.id,
      filename: data.filename,
    });

    return await getPresignedUploadUrl({
      key,
      contentType: data.contentType,
    });
  });

export const updateEngResourceFn = createServerFn({
  method: "POST",
})
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(FinalizeUpdateEngResourceSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceById, updateEngagementResource } =
      await import("~/lib/server/engagement");
    const { buildKeyFromResourceId, deleteObjectByKey } =
      await import("~/lib/server/s3");
    const coachId = context.coach.id;
    const resource = await getEngagementResourceById(data.resourceId, coachId);

    const isSwitchingToUrl =
      (resource.uploadModel === "file" ||
        resource.uploadModel === "agreement") &&
      data.uploadModel === "url";

    // Handle S3 file deletion when switching from file to URL
    if (
      isSwitchingToUrl &&
      resource.filename &&
      resource.uploadModel !== "url"
    ) {
      const oldKey = buildKeyFromResourceId({
        coachId,
        engagementId: resource.engagementId,
        resourceId: resource.id,
        filename: resource.filename,
      });

      try {
        await deleteObjectByKey(oldKey);
      } catch (error) {
        console.error("S3 file deletion failed during resource update", {
          error: error instanceof Error ? error.message : String(error),
          resourceId: data.resourceId,
          coachId,
          engagementId: resource.engagementId,
          filename: resource.filename,
          operation: "updateEngResource",
          timestamp: new Date().toISOString(),
        });
        // Continue with update even if S3 deletion fails
      }
    }

    const isSwitchingToFile =
      resource.uploadModel === "url" &&
      (data.uploadModel === "file" || data.uploadModel === "agreement");

    return await updateEngagementResource(
      data.resourceId,
      coachId,
      {
        title: data.title,
        filename: isSwitchingToUrl
          ? null // Remove filename when switching to URL
          : data.uploadModel === "url"
            ? resource.filename
            : (data.newName ?? resource.filename),
        externalLink: isSwitchingToFile
          ? null // Remove externalLink when switching from URL to file
          : data.uploadModel === "url"
            ? data.externalLinkUrl
            : resource.externalLink,
        expirationDate:
          (data.uploadModel ?? resource.uploadModel) === "agreement"
            ? (data.expirationDate ?? resource.expirationDate)
            : null,
        uploadModel: data.uploadModel,
        publishedAt: new Date(),
      },
      data.tagIds,
    );
  });

export const getEngResourceDownloadInfoFn = createServerFn({ method: "GET" })
  .middleware([S3Middleware, authOrCoachMiddleware])
  .validator(GetEngResourceDownloadInfoSchema)
  .handler(async ({ data, context }) => {
    const { assertEngagementResourceViewer, getEngagementResourceById } =
      await import("~/lib/server/engagement");
    const { buildKeyFromResourceId, getPresignedReadUrl } =
      await import("~/lib/server/s3");
    await assertEngagementResourceViewer(data.resourceId, {
      userId: context.userId,
      coachId: context.coachId,
    });

    const resource = await getEngagementResourceById(data.resourceId);

    if (resource.uploadModel === "url") {
      return {
        resourceId: resource.id,
        filename: "",
        fileUrl: resource.externalLink,
      };
    }

    const key = resource.filename
      ? buildKeyFromResourceId({
          coachId: resource.coachId,
          engagementId: resource.engagementId,
          resourceId: resource.id,
          filename: resource.filename,
        })
      : null;

    if (!key) {
      // Resource has no file (was deleted or never had one)
      return {
        resourceId: resource.id,
        filename: "",
        fileUrl: null,
      };
    }

    const signed = await getPresignedReadUrl({ key, expiresIn: 200 });

    let filename = resource.filename;
    if (!filename && resource.externalLink) {
      const u = new URL(resource.externalLink);
      const last = u.pathname.split("/").filter(Boolean).pop() || "file";
      filename = decodeURIComponent(last);
    }

    return {
      resourceId: resource.id,
      filename,
      fileUrl: signed.url,
    };
  });

export const deleteEngResourceAndFileFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(DeleteEngResourceSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceById, deleteEngagementResource } =
      await import("~/lib/server/engagement");
    const { buildKeyFromResourceId, deleteObjectByKey } =
      await import("~/lib/server/s3");
    const coachId = context.coach.id;
    const existing = await getEngagementResourceById(data.resourceId, coachId);

    const oldKey = existing.filename
      ? buildKeyFromResourceId({
          coachId,
          engagementId: existing.engagementId,
          resourceId: existing.id,
          filename: existing.filename,
        })
      : null;

    if (oldKey && existing.uploadModel !== "url") {
      try {
        await deleteObjectByKey(oldKey);
      } catch {
        throw new Error("S3 delete resource failed");
      }
    }

    await deleteEngagementResource(data.resourceId, coachId);
    return { ok: true };
  });

export const getAllResourcesFn = createServerFn({ method: "GET" })
  .middleware([authOrCoachMiddleware])
  .validator(GetAllEngagementResourcesSchema)
  .handler(async ({ data, context }) => {
    const { checkEngagementByUserOrCoach, getAllResources } =
      await import("~/lib/server/engagement");
    await checkEngagementByUserOrCoach(
      data.engagementId,
      context.userId,
      context.coachId,
    );
    return getAllResources(data.engagementId);
  });

export const getAllResourceByUploadModelFn = createServerFn({ method: "GET" })
  .middleware([authOrCoachMiddleware])
  .validator(GetAllEngagementResourcesByUploadModelSchema)
  .handler(async ({ data, context }) => {
    const { checkEngagementByUserOrCoach, getAllResourceByUploadModel } =
      await import("~/lib/server/engagement");
    await checkEngagementByUserOrCoach(
      data.engagementId,
      context.userId,
      context.coachId,
    );
    return getAllResourceByUploadModel(data.engagementId, data.uploadModel);
  });

export const getEngagementResourcesByCoachFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(EngagementResourcesByCoachSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResources } = await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getEngagementResources("coach", coachId, data.engagementId, {
      page: data.page,
      limit: data.limit,
      tagIds: data.tagIds,
    });
  });

export const getEngagementResourcesByClientFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .validator(EngagementResourcesByCoachSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResources } = await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getEngagementResources("user", userId, data.engagementId, {
      page: data.page,
      limit: data.limit,
      tagIds: data.tagIds,
    });
  });

export const getEngagementResourceTagsByCoachFn = createServerFn({
  method: "GET",
})
  .middleware([requireClerkCoachAuth])
  .validator(EngagementResourceTagsSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceTags } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getEngagementResourceTags("coach", coachId, data.engagementId);
  });

export const getEngagementResourceTagsByClientFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .validator(EngagementResourceTagsSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceTags } =
      await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getEngagementResourceTags("user", userId, data.engagementId);
  });

export const getEngagementAgreementsByCoachFn = createServerFn({
  method: "GET",
})
  .middleware([requireClerkCoachAuth])
  .validator(EngagementResourcesByCoachSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementAgreements } = await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getEngagementAgreements("coach", coachId, data.engagementId, {
      page: data.page,
      limit: data.limit,
    });
  });

export const getEngagementAgreementByClientFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .validator(EngagementResourcesByCoachSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementAgreements } = await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getEngagementAgreements("user", userId, data.engagementId, {
      page: data.page,
      limit: data.limit,
    });
  });

export const getEngagementResourceByIdFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetEngResourceDownloadInfoSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementResourceById } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getEngagementResourceById(data.resourceId, coachId);
  });

export const getEngagementResourceByIdByClientFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .validator(GetEngResourceDownloadInfoSchema)
  .handler(async ({ data, context }) => {
    const { assertEngagementResourceViewer, getEngagementResourceById } =
      await import("~/lib/server/engagement");
    await assertEngagementResourceViewer(data.resourceId, {
      userId: context.user.id,
    });
    return getEngagementResourceById(data.resourceId);
  });

export const getUserInfoForCoachEngagementFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(UserInfoForEngagementSchema)
  .handler(async ({ data, context }) => {
    const { getUserInfoForEngagement } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getUserInfoForEngagement("coach", coachId, data.engagementId);
  });

export const getUserInfoForClientEngagementFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .validator(UserInfoForEngagementSchema)
  .handler(async ({ data, context }) => {
    const { getPersonalUserInfoForEngagement } =
      await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getPersonalUserInfoForEngagement(userId, data.engagementId);
  });

export const getCoachInfoForEngagementFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(UserInfoForEngagementSchema)
  .handler(async ({ data, context }) => {
    const { getUserInfoForEngagement } =
      await import("~/lib/server/engagement");
    const userId = context.user.id;
    return getUserInfoForEngagement("user", userId, data.engagementId);
  });

export const updateEngagementStatusFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(UpdateEngagementStatusSchema)
  .handler(async ({ data, context }) => {
    const { updateEngagementStatus } = await import("~/lib/server/engagement");
    await updateEngagementStatus(
      context.coach,
      data.engagementId,
      data.status as EngagementStatus,
      undefined,
      data.coachTimeZone,
    );
    return { ok: true };
  });

export const getPaymentInfoFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(PaymentInfoForEngagementSchema)
  .handler(async ({ data, context }) => {
    const { getPaymentInfo } = await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getPaymentInfo(coachId, data.engagementId);
  });

export const getPaymentHistoryFn = createServerFn({ method: "POST" })
  .middleware([authOrCoachMiddleware])
  .validator(PaymentHistoryForEngagementSchema)
  .handler(async ({ data, context }) => {
    const { checkEngagementByUserOrCoach, getPaymentHistory } =
      await import("~/lib/server/engagement");
    const { participantId, participantType } =
      await checkEngagementByUserOrCoach(
        data.engagementId,
        context.userId,
        context.coachId,
      );
    return getPaymentHistory(
      participantType,
      participantId,
      data.engagementId,
      data.filter,
      {
        page: data.page,
        limit: data.limit,
      },
    );
  });

export const editEngagementInvoiceFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(EditEngagementInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementInvoiceRef, updateCoachInvoice } =
      await import("~/lib/server/invoice");
    const ref = await getEngagementInvoiceRef({
      coachId: context.coach.id,
      engagementId: data.engagementId,
      invoiceId: data.id,
    });
    return updateCoachInvoice(ref, {
      newDueDate: data.invoiceDate,
      description: data.description,
      newAmount: data.invoiceAmount,
    });
  });

export const deleteInvoiceFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(DeleteEngagementInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementInvoiceRef, deleteCoachInvoice } =
      await import("~/lib/server/invoice");
    const ref = await getEngagementInvoiceRef({
      coachId: context.coach.id,
      engagementId: data.engagementId,
      invoiceId: data.id,
    });
    return deleteCoachInvoice(ref);
  });

export const cancelInvoiceFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(DeleteEngagementInvoiceSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementInvoiceRef, voidCoachInvoice } =
      await import("~/lib/server/invoice");
    const ref = await getEngagementInvoiceRef({
      coachId: context.coach.id,
      engagementId: data.engagementId,
      invoiceId: data.id,
    });
    const result = await voidCoachInvoice(ref);

    // Send notification to user that their invoice was cancelled
    const { getDBWithInit } = await import("~/lib/db");
    const { createAndSendNotification } =
      await import("~/lib/server/notification");
    const { NotificationType } = await import("../../../drizzle/schema");
    const db = await getDBWithInit();

    const engagement = await db.query.engagements.findFirst({
      where: { id: data.engagementId, coachId: context.coach.id },
      with: {
        coach: true,
        user: true,
      },
    });

    if (engagement?.user?.email && engagement.coach) {
      await createAndSendNotification(
        {
          userId: engagement.userId,
          coachId: engagement.coachId,
          actorCoachId: engagement.coachId,
          type: NotificationType.INVOICE_CHANGED,
          title: "Your invoice has been cancelled",
          body: `An invoice from coach ${engagement.coach.firstName} ${engagement.coach.lastName} has been cancelled.`,
          relatedEntityType: "invoice",
          relatedEntityId: data.id,
          link: buildNotificationLink(
            linkOptions({
              to: "/coaching/$engagementId/admin",
              params: { engagementId: engagement.id },
            }),
          ),
          recipient: "user",
        },
        {
          coachName: `${engagement.coach.firstName} ${engagement.coach.lastName}`,
          email: engagement.user.email,
        },
        db,
      );
    }

    return result;
  });

export const getEngagementSessionProgressFn = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .validator(GetEngagementSessionProgressSchema)
  .handler(async ({ data, context }) => {
    const { getEngagementSessionProgress } =
      await import("~/lib/server/engagement");
    const coachId = context.coach.id;
    return getEngagementSessionProgress(coachId, data.engagementId);
  });
